import React, { useState } from "react";
import { ArrowUp, X, Sparkles } from "lucide-react";
import MessageContent from "./MessageContent";
import ModelSwitcher, { ModelBadge } from "./ModelSwitcher";
import { API } from "../lib/api";
import { MODEL_FAMILIES } from "../constants/models";

const DEFAULT_KEYS = ["gpt-5.4", "claude-sonnet-4-6", "gemini-3.1-pro"];

export default function ComparePanel({ models, onExit }) {
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [cols, setCols] = useState(
    DEFAULT_KEYS.map((k) => ({ modelKey: k, text: "", streaming: false }))
  );

  const setColModel = (i, key) =>
    setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, modelKey: key } : c)));

  const streamInto = async (i, modelKey, content) => {
    setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, text: "", streaming: true } : c)));
    let acc = "";
    try {
      const resp = await fetch(`${API}/compare/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: modelKey, content }),
      });
      if (!resp.ok || !resp.body) throw new Error("failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          const data = JSON.parse(line);
          if (data.delta) {
            acc += data.delta;
            setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, text: acc } : c)));
          } else if (data.error) {
            acc += `\n\n_Error: ${data.error}_`;
            setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, text: acc } : c)));
          }
        }
      }
    } catch (e) {
      setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, text: "Failed to respond." } : c)));
    } finally {
      setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, streaming: false } : c)));
    }
  };

  const run = async () => {
    const content = prompt.trim();
    if (!content || running) return;
    setRunning(true);
    await Promise.all(cols.map((c, i) => streamInto(i, c.modelKey, content)));
    setRunning(false);
  };

  const hasOutput = cols.some((c) => c.text || c.streaming);

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="compare-panel">
      {/* Prompt bar */}
      <div className="px-4 pt-4">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-2xl border border-[#E6E6DF] bg-white shadow-[0_2px_20px_rgba(23,23,22,0.05)] p-2.5 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#2D6A4F] shrink-0 ml-1.5" />
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="Ask all three models at once…"
              data-testid="compare-prompt-input"
              className="flex-1 bg-transparent py-2 text-[15px] placeholder:text-[#8C8C85] focus:outline-none"
            />
            <button
              onClick={run}
              disabled={running || !prompt.trim()}
              data-testid="compare-run-button"
              className="h-9 w-9 rounded-full flex items-center justify-center bg-[#1F2421] text-white disabled:opacity-30 hover:bg-[#2D6A4F]"
              style={{ transition: "background-color 0.2s ease, opacity 0.2s ease" }}
            >
              <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>

      {/* Columns */}
      <div className="flex-1 overflow-hidden px-4 pb-4 pt-4">
        <div className="mx-auto max-w-5xl h-full grid grid-cols-1 md:grid-cols-3 gap-4">
          {cols.map((col, i) => {
            const mdl = models.find((m) => m.key === col.modelKey);
            const fam = MODEL_FAMILIES[mdl?.family || "openai"];
            return (
              <div
                key={i}
                className="flex flex-col min-h-0 rounded-2xl border bg-white overflow-hidden"
                style={{ borderColor: fam.border }}
                data-testid={`compare-column-${i}`}
              >
                <div className="flex items-center justify-between px-3 py-2.5 border-b" style={{ borderColor: fam.border, background: fam.badgeBg }}>
                  <ModelSwitcher models={models} value={col.modelKey} onChange={(k) => setColModel(i, k)} />
                </div>
                <div className="flex-1 overflow-y-auto omni-scroll p-4">
                  {col.text ? (
                    <>
                      <MessageContent content={col.text} />
                      {col.streaming && <span className="omni-cursor" />}
                    </>
                  ) : col.streaming ? (
                    <div className="flex gap-1.5 py-2">
                      <span className="omni-dot" />
                      <span className="omni-dot" style={{ animationDelay: "0.2s" }} />
                      <span className="omni-dot" style={{ animationDelay: "0.4s" }} />
                    </div>
                  ) : (
                    <p className="text-sm text-[#8C8C85]">Response will appear here.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
